import Cookie from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { SIGNUP_SESSION } from '../../helpers/graphql/signupPayment';
import graphQLClient from '../../helpers/graphQLClient';
import { getUser } from '../../helpers/token';
import { figtree } from '../Landing/fonts';
import { INK } from '../Landing/ui';

const DISMISS_STORAGE_KEY = 'signup_banner_dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const SignupSessionBanner = () => {
  const router = useRouter();
  const [dismissed, setDismissed] = useState(true); // Start hidden to prevent flash
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for cookie and fetch session on client side
  useEffect(() => {
    // Don't run on /join/* pages
    if (router.pathname.startsWith('/join')) {
      setLoading(false);
      return;
    }

    const cookie = Cookie.get('signup_session');
    if (!cookie) {
      setLoading(false);
      return;
    }

    // Check localStorage for recent dismissal
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION_MS) {
      setDismissed(true);
      setLoading(false);
      return;
    }

    setDismissed(false);

    // Fetch session using graphQLClient
    graphQLClient(SIGNUP_SESSION)
      .then(data => {
        if (data?.signupSession) {
          setSession(data.signupSession);
        }
      })
      .catch(err => {
        console.error('Failed to fetch signup session:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [router.pathname]);

  // Don't show on /join/* pages
  if (router.pathname.startsWith('/join')) return null;

  // Don't show if dismissed
  if (dismissed) return null;

  // Don't show while loading
  if (loading) return null;

  // Don't show if no session data
  if (!session) return null;

  // Don't show if user is logged in
  const user = getUser();
  if (user) return null;

  const { stage, username, profileImage, createdAt, authMethod } = session;

  // Calculate days remaining (14-day session)
  const sessionAge = Date.now() - new Date(createdAt).getTime();
  const daysRemaining = Math.max(
    1,
    14 - Math.floor(sessionAge / (24 * 60 * 60 * 1000)),
  );

  // Stage-to-route mapping
  const getRoute = (stg, auth) => {
    const routeMap = {
      email_verified: '/join/setup',
      oauth_verified: '/join/setup',
      profile_set: '/join/preview',
      preview_seen: auth === 'google' ? '/join/plan' : '/join/password',
      details_complete: '/join/plan',
      payment_pending: '/join/plan',
    };
    return routeMap[stg] || '/join';
  };

  // Stage-to-copy mapping
  const getCopy = stg => {
    const copyMap = {
      email_verified: {
        text: 'Welcome back! Finish setting up your blog.',
        cta: 'Continue Setup',
      },
      oauth_verified: {
        text: 'Welcome back! Finish setting up your blog.',
        cta: 'Continue Setup',
      },
      profile_set: {
        text: `@${username} is yours for ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}. Finish setup.`,
        cta: 'See Preview',
      },
      preview_seen: {
        text: `@${username} is yours for ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}. One step left!`,
        cta: authMethod === 'google' ? 'Choose Plan' : 'Set Password',
      },
      details_complete: {
        text: `@${username} is reserved for ${daysRemaining} more ${daysRemaining === 1 ? 'day' : 'days'}`,
        cta: 'Choose Plan',
      },
      payment_pending: {
        text: `@${username} is reserved for ${daysRemaining} more ${daysRemaining === 1 ? 'day' : 'days'}`,
        cta: 'Choose Plan',
      },
    };

    return copyMap[stg] || null;
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    setDismissed(true);
  };

  const handleContinue = () => {
    router.push(getRoute(stage, authMethod));
  };

  const copy = getCopy(stage);

  // Don't show banner for stages without copy (e.g., email_pending)
  if (!copy) return null;

  const { text, cta } = copy;

  // Ticket-stub treatment: paper bar with a dashed "tear here" perforation.
  return (
    <div
      className={`${figtree.className} signup-flow tw-fixed tw-bottom-14 lg:tw-bottom-0 tw-left-0 tw-right-0 tw-z-[1001] tw-border-0 tw-border-t tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-top-width:2px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-px-4 tw-py-3 tw-shadow-[0_-12px_30px_-18px_rgba(29,45,40,0.45)]`}
    >
      <div className="tw-mx-auto tw-flex tw-max-w-4xl tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 sm:tw-flex-nowrap sm:tw-gap-4">
        {/* Dismiss Button - on the left */}
        <button
          type="button"
          onClick={handleDismiss}
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-1.5 tw-text-[#8A958F] tw-transition-colors hover:tw-text-[#1D2D28] dark:hover:tw-text-[#EBE6D9]"
          aria-label="Dismiss"
        >
          <svg
            className="tw-h-5 tw-w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Avatar in a thin postcard mat (if available) */}
        {profileImage && (
          <img
            src={profileImage}
            alt=""
            className="tw-hidden tw-h-8 tw-w-8 tw-rounded-full tw-border-2 tw-border-solid tw-border-[#E7E0CF] tw-bg-white tw-object-cover sm:tw-block"
          />
        )}

        {/* Message */}
        <span
          className={`${INK} tw-text-center tw-text-sm sm:tw-text-left sm:tw-text-[15px]`}
        >
          {text}
        </span>

        {/* CTA Button */}
        <button
          type="button"
          onClick={handleContinue}
          className="tw-cursor-pointer tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-bg-primary-600 tw-px-4 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-white tw-shadow-[0_10px_30px_-10px_rgba(0,137,123,0.7)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-bg-primary-700"
        >
          {cta} →
        </button>
      </div>
    </div>
  );
};

export default SignupSessionBanner;
